/**
 * Reminders route.
 *
 * Lists `users/{uid}/reminders` (via `useReminders`) grouped into Atrasados /
 * Próximos / Concluídos. New reminders are created through the `createReminder`
 * callable (which expects an ISO due date); marking done or cancelling is a
 * direct Firestore status update.
 *
 * Kept mounted at path '/reminders'. Exported as `RemindersRoute`.
 */
import type { ReminderDoc, ReminderStatus } from '@agente/shared'
import { useNavigate } from '@tanstack/react-router'
import { Bell, Check, Loader, MessageSquare, Plus, X } from 'lucide-react'
import { type FormEvent, type ReactNode, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { RoutineList } from '@/components/reminders/routine-list'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useReminders } from '@/hooks/useReminders'
import { useAuth } from '@/lib/auth'
import { createReminder } from '@/lib/callables'
import {
  type BatchStatusResult,
  updateReminderStatus,
  updateReminderStatusBatch,
} from '@/lib/firestore'
import { formatDateTime, formatRelative, fromDateTimeLocalValue } from '@/lib/format'

/** A partir desse número de itens num grupo, mostramos as ações em massa no cabeçalho. */
const BULK_ACTIONS_MIN_COUNT = 2

/** "1 lembrete aceito" / "2 lembretes aceitos" — concordância de número simples (regular). */
function pluralizeReminders(count: number, participle: string): string {
  const noun = count === 1 ? 'lembrete' : 'lembretes'
  const suffix = count === 1 ? participle : `${participle}s`
  return `${count} ${noun} ${suffix}`
}

/** Toast padrão para o resultado de uma ação em massa, tratando falha parcial sem travar a UI. */
function reportBatchResult(result: BatchStatusResult, participle: string, actionLabel: string) {
  const { succeeded, failed } = result
  if (failed === 0) {
    toast.success(pluralizeReminders(succeeded, participle))
    return
  }
  if (succeeded === 0) {
    toast.error(`Não foi possível ${actionLabel}`, {
      description: `${failed} ${failed === 1 ? 'lembrete falhou' : 'lembretes falharam'}.`,
    })
    return
  }
  toast.warning(pluralizeReminders(succeeded, participle), {
    description: `${failed} ${failed === 1 ? 'lembrete falhou' : 'lembretes falharam'} — tente novamente.`,
  })
}

/** A reminder counts as "closed" when marked done, cancelled or dismissed. */
function isClosed(status: ReminderDoc['status']): boolean {
  return status === 'done' || status === 'cancelled' || status === 'dismissed'
}

export function RemindersRoute() {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const { reminders, loading, error } = useReminders()
  const now = Date.now()

  const { suggested, overdue, upcoming, sent, closed } = useMemo(() => {
    const suggested: ReminderDoc[] = []
    const overdue: ReminderDoc[] = []
    const upcoming: ReminderDoc[] = []
    const sent: ReminderDoc[] = []
    const closed: ReminderDoc[] = []
    for (const r of reminders) {
      if (r.status === 'suggested') {
        suggested.push(r)
      } else if (r.status === 'sent') {
        // Histórico de enviados: não vaza para Atrasados/Próximos.
        sent.push(r)
      } else if (isClosed(r.status)) {
        closed.push(r)
      } else if (r.dueAt < now) {
        overdue.push(r)
      } else {
        upcoming.push(r)
      }
    }
    // `useReminders` orders by dueAt asc; keep history newest-first.
    sent.sort((a, b) => (b.sentAt ?? b.dueAt) - (a.sentAt ?? a.dueAt))
    closed.sort((a, b) => b.dueAt - a.dueAt)
    return { suggested, overdue, upcoming, sent, closed }
  }, [reminders, now])

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 py-6 md:p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Lembretes</h1>
          <p className="text-muted-foreground text-sm">
            Seus lembretes e follow-ups, entregues no Teams.
          </p>
        </div>
        <CreateReminderDialog />
      </header>

      {error ? (
        <p className="text-destructive text-sm">Não foi possível carregar os lembretes: {error}</p>
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : reminders.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-6">
          <ReminderGroup
            title="Sugeridos"
            tone="suggested"
            reminders={suggested}
            headerActions={
              uid && suggested.length >= BULK_ACTIONS_MIN_COUNT ? (
                <SuggestedBulkActions uid={uid} reminders={suggested} />
              ) : null
            }
          />
          <ReminderGroup
            title="Atrasados"
            tone="overdue"
            reminders={overdue}
            headerActions={
              uid && overdue.length >= BULK_ACTIONS_MIN_COUNT ? (
                <OverdueBulkActions uid={uid} reminders={overdue} />
              ) : null
            }
          />
          <ReminderGroup title="Próximos" tone="upcoming" reminders={upcoming} />
          <ReminderGroup title="Enviados" tone="sent" reminders={sent} />
          <ReminderGroup title="Concluídos" tone="closed" reminders={closed} />
        </div>
      )}

      <RoutineList />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
      <Bell className="text-muted-foreground size-8" aria-hidden="true" />
      <p className="text-muted-foreground text-sm">
        Nenhum lembrete ainda. Crie o primeiro ou peça ao agente na conversa.
      </p>
    </div>
  )
}

type ReminderTone = 'suggested' | 'overdue' | 'upcoming' | 'sent' | 'closed'

function ReminderGroup({
  title,
  tone,
  reminders,
  headerActions,
}: {
  title: string
  tone: ReminderTone
  reminders: ReminderDoc[]
  headerActions?: ReactNode
}) {
  if (reminders.length === 0) return null
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          {title}
          <span className="ml-1.5 font-normal">({reminders.length})</span>
        </h2>
        {headerActions}
      </div>
      <ul className="flex flex-col gap-2">
        {reminders.map((r) => (
          <ReminderRow key={r.id} reminder={r} tone={tone} />
        ))}
      </ul>
    </section>
  )
}

/**
 * Ações em massa para o grupo "Sugeridos": aceitar todos (→ scheduled, sem
 * confirmação — mesma UX do botão individual) ou descartar todos (→ dismissed,
 * com confirmação prévia por ser uma ação de "apagar em lote"). Persiste com o
 * mesmo mecanismo das ações por item (escrita direta no Firestore), só que via
 * `updateReminderStatusBatch` para cobrir a lista inteira num `writeBatch`.
 */
function SuggestedBulkActions({ uid, reminders }: { uid: string; reminders: ReminderDoc[] }) {
  const [pending, setPending] = useState<ReminderStatus | null>(null)

  async function runBatch(status: ReminderStatus, participle: string, actionLabel: string) {
    setPending(status)
    try {
      const result = await updateReminderStatusBatch(
        uid,
        reminders.map((r) => r.id),
        status,
      )
      reportBatchResult(result, participle, actionLabel)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error(`Não foi possível ${actionLabel}`, { description: message })
    } finally {
      setPending(null)
    }
  }

  function handleDiscardAll() {
    const confirmed = window.confirm(
      `Descartar todos os ${reminders.length} lembretes sugeridos? Eles saem da lista de sugestões.`,
    )
    if (!confirmed) return
    void runBatch('dismissed', 'descartado', 'descartar os lembretes')
  }

  const busy = pending !== null
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => void runBatch('scheduled', 'aceito', 'aceitar os lembretes')}
      >
        {pending === 'scheduled' ? <Loader className="size-4 animate-spin" /> : null}
        Aceitar todos
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleDiscardAll}>
        {pending === 'dismissed' ? <Loader className="size-4 animate-spin" /> : null}
        Descartar todos
      </Button>
    </div>
  )
}

/**
 * Mesmo padrão de ações em massa para "Atrasados": concluir todos (sem
 * confirmação) ou cancelar todos (com confirmação, equivalente a descartar).
 */
function OverdueBulkActions({ uid, reminders }: { uid: string; reminders: ReminderDoc[] }) {
  const [pending, setPending] = useState<ReminderStatus | null>(null)

  async function runBatch(status: ReminderStatus, participle: string, actionLabel: string) {
    setPending(status)
    try {
      const result = await updateReminderStatusBatch(
        uid,
        reminders.map((r) => r.id),
        status,
      )
      reportBatchResult(result, participle, actionLabel)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error(`Não foi possível ${actionLabel}`, { description: message })
    } finally {
      setPending(null)
    }
  }

  function handleCancelAll() {
    const confirmed = window.confirm(
      `Cancelar todos os ${reminders.length} lembretes atrasados? Eles saem da lista de atrasados.`,
    )
    if (!confirmed) return
    void runBatch('cancelled', 'cancelado', 'cancelar os lembretes')
  }

  const busy = pending !== null
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => void runBatch('done', 'concluído', 'concluir os lembretes')}
      >
        {pending === 'done' ? <Loader className="size-4 animate-spin" /> : null}
        Concluir todos
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleCancelAll}>
        {pending === 'cancelled' ? <Loader className="size-4 animate-spin" /> : null}
        Cancelar todos
      </Button>
    </div>
  )
}

function ReminderRow({ reminder, tone }: { reminder: ReminderDoc; tone: ReminderTone }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const uid = user?.uid ?? null
  const [pending, setPending] = useState(false)
  const done = isClosed(reminder.status)
  const suggested = reminder.status === 'suggested'
  const sent = reminder.status === 'sent'

  async function setStatus(status: ReminderDoc['status']) {
    if (!uid) return
    setPending(true)
    try {
      await updateReminderStatus(uid, reminder.id, status)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível atualizar o lembrete', { description: message })
    } finally {
      setPending(false)
    }
  }

  return (
    <li className="border-border bg-card flex items-start gap-3 rounded-lg border p-3 shadow-sm">
      <div className="min-w-0 flex-1">
        <p
          className={
            done ? 'text-muted-foreground text-sm font-medium line-through' : 'text-sm font-medium'
          }
        >
          {reminder.title}
          {reminder.urgent ? (
            <span className="text-destructive ml-2 inline-flex items-center gap-1 align-middle text-xs font-semibold">
              <Bell className="size-3" aria-hidden="true" />
              Urgente
            </span>
          ) : null}
        </p>
        {reminder.description ? (
          <p className="text-muted-foreground mt-0.5 text-sm">{reminder.description}</p>
        ) : null}
        <p className="mt-1 flex items-center gap-1.5 text-xs">
          <span
            className={
              tone === 'overdue' ? 'text-destructive font-medium' : 'text-muted-foreground'
            }
          >
            {formatDateTime(reminder.dueAt)}
          </span>
          {sent ? (
            <span className="text-muted-foreground">
              · enviado{reminder.sentAt ? ` ${formatRelative(reminder.sentAt)}` : ''}
            </span>
          ) : !done ? (
            <span className="text-muted-foreground">· {formatRelative(reminder.dueAt)}</span>
          ) : reminder.status === 'cancelled' ? (
            <span className="text-muted-foreground">· cancelado</span>
          ) : reminder.status === 'dismissed' ? (
            <span className="text-muted-foreground">· descartado</span>
          ) : (
            <span className="text-muted-foreground">· concluído</span>
          )}
        </p>
        {reminder.source === 'email' ? (
          <p className="text-muted-foreground mt-0.5 text-xs">Sugerido a partir de e-mail</p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Conversar com o agente sobre ${reminder.title}`}
          title="Conversar com o agente"
          onClick={() => void navigate({ to: '/', search: { reminderId: reminder.id } })}
        >
          <MessageSquare className="size-4" />
        </Button>
        {suggested ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Aceitar ${reminder.title}`}
              disabled={pending}
              onClick={() => void setStatus('scheduled')}
            >
              {pending ? <Loader className="size-4 animate-spin" /> : <Check className="size-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Descartar ${reminder.title}`}
              disabled={pending}
              onClick={() => void setStatus('dismissed')}
            >
              <X className="size-4" />
            </Button>
          </>
        ) : !done && !sent ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Concluir ${reminder.title}`}
              disabled={pending}
              onClick={() => void setStatus('done')}
            >
              {pending ? <Loader className="size-4 animate-spin" /> : <Check className="size-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Cancelar ${reminder.title}`}
              disabled={pending}
              onClick={() => void setStatus('cancelled')}
            >
              <X className="size-4" />
            </Button>
          </>
        ) : null}
      </div>
    </li>
  )
}

function CreateReminderDialog() {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('')
  const [description, setDescription] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [saving, setSaving] = useState(false)

  const canSave = title.trim().length > 0 && due.length > 0 && !saving

  function reset() {
    setTitle('')
    setDue('')
    setDescription('')
    setUrgent(false)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSave) return
    const dueMillis = fromDateTimeLocalValue(due)
    if (dueMillis === null) {
      toast.error('Data inválida', { description: 'Informe uma data e hora válidas.' })
      return
    }
    setSaving(true)
    try {
      await createReminder({
        title: title.trim(),
        dueISO: new Date(dueMillis).toISOString(),
        description: description.trim() || undefined,
        urgent: urgent || undefined,
      })
      toast.success('Lembrete criado')
      reset()
      setOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível criar o lembrete', { description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" className="shrink-0 shadow-sm">
          <Plus className="size-4" />
          Novo lembrete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Novo lembrete</DialogTitle>
            <DialogDescription>O agente avisa no Teams no horário definido.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reminder-title">Título</Label>
            <Input
              id="reminder-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Revisar o contrato da Acme"
              required
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reminder-due">Data e hora</Label>
            <Input
              id="reminder-due"
              type="datetime-local"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reminder-description">Descrição (opcional)</Label>
            <Textarea
              id="reminder-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhes ou contexto do lembrete."
              rows={3}
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <Label htmlFor="reminder-urgent" className="flex flex-col items-start gap-0.5">
              <span>Urgente</span>
              <span className="text-muted-foreground text-xs font-normal">
                Reforça a entrega do lembrete.
              </span>
            </Label>
            <Switch id="reminder-urgent" checked={urgent} onCheckedChange={setUrgent} />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSave}>
              {saving ? <Loader className="size-4 animate-spin" /> : null}
              Criar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default RemindersRoute
